/**
 * Compile Gate Filters Component
 * 
 * Provides search, severity/rule filters, grouping, and view mode controls.
 */

import React from 'react';
import { Search } from 'lucide-react';
import { DarkPanel } from '@/components/ui/DarkPanel';
import { Input } from '@/components/ui/input';

interface CompileGateFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedSeverities: string[];
  onSeverityToggle: (severity: string) => void;
  selectedRules: string[];
  onRuleToggle: (ruleId: string) => void;
  groupBy: 'none' | 'rule' | 'severity' | 'wbs';
  onGroupByChange: (mode: 'none' | 'rule' | 'severity' | 'wbs') => void;
  showReviewed: boolean;
  onShowReviewedToggle: () => void;
  compactMode: boolean;
  onCompactModeToggle: () => void;
  reviewMode: boolean;
  onReviewModeToggle: () => void;
  uniqueSeverities: string[];
  uniqueRules: Array<{ id: string; name: string }>;
  hasWBS: boolean;
}

export const CompileGateFilters: React.FC<CompileGateFiltersProps> = ({
  searchQuery,
  onSearchChange,
  selectedSeverities,
  onSeverityToggle,
  selectedRules,
  onRuleToggle,
  groupBy,
  onGroupByChange,
  showReviewed,
  onShowReviewedToggle,
  compactMode,
  onCompactModeToggle,
  reviewMode,
  onReviewModeToggle,
  uniqueSeverities,
  uniqueRules,
  hasWBS
}) => {
  return (
    <div className="w-full" style={{ width: '100%', maxWidth: 'none', minWidth: 0, boxSizing: 'border-box' }}>
      <DarkPanel className="p-4 sticky top-0 z-10 w-full" style={{ width: '100%', maxWidth: 'none', minWidth: 0, boxSizing: 'border-box' }}>
        <div className="space-y-4" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          {/* Top Row: Search and Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-nb-text-muted" />
              <Input
                placeholder="Search violations..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={compactMode}
                  onChange={onCompactModeToggle}
                  className="rounded"
                />
                <span className="text-nb-text-muted">Compact</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={showReviewed}
                  onChange={onShowReviewedToggle}
                  className="rounded"
                />
                <span className="text-nb-text-muted">Show Reviewed</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={reviewMode}
                  onChange={onReviewModeToggle}
                  className="rounded"
                />
                <span className="text-nb-text-muted">Review Mode</span>
              </label>
            </div>
          </div>

        {/* Filter Row: Severity, Rule, Group By */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
          {/* Severity Multi-Select */}
          <div>
            <div className="text-xs text-nb-text-muted mb-2 block">Severity</div>
            <div className="flex flex-wrap gap-2">
              {uniqueSeverities.map(severity => {
                const isSelected = selectedSeverities.includes(severity);
                return (
                  <button
                    key={severity}
                    onClick={() => onSeverityToggle(severity)}
                    className={`px-2 py-1 text-xs rounded border transition-colors ${
                      isSelected
                        ? 'bg-nb-accent/20 border-nb-accent text-nb-accent'
                        : 'bg-nb-surface-2 border-nb-border-subtle text-nb-text-muted hover:border-nb-border-strong'
                    }`}
                  >
                    {severity.charAt(0).toUpperCase() + severity.slice(1)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Rule Multi-Select */}
          <div>
            <div className="text-xs text-nb-text-muted mb-2 block">Rule</div>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              {uniqueRules.map(rule => {
                const isSelected = selectedRules.includes(rule.id);
                return (
                  <button
                    key={rule.id}
                    onClick={() => onRuleToggle(rule.id)}
                    className={`px-2 py-1 text-xs rounded border transition-colors whitespace-normal break-words ${
                      isSelected
                        ? 'bg-nb-accent/20 border-nb-accent text-nb-accent'
                        : 'bg-nb-surface-2 border-nb-border-subtle text-nb-text-muted hover:border-nb-border-strong'
                    }`}
                    title={rule.name}
                  >
                    {rule.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Group By */}
          <div>
            <div className="text-xs text-nb-text-muted mb-2 block">Group By</div>
            <select
              value={groupBy}
              onChange={(e) => onGroupByChange(e.target.value as any)}
              className="w-full px-3 py-2 bg-nb-surface-2 border border-nb-border-subtle rounded text-sm text-nb-text-primary"
            >
              <option value="none">None</option>
              <option value="rule">Rule</option>
              <option value="severity">Severity</option>
              {hasWBS && <option value="wbs">WBS</option>}
            </select>
          </div>
        </div>
        </div>
      </DarkPanel>
    </div>
  );
};
